import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Link } from "react-router-dom";
import { LuCheck } from "react-icons/lu";
import { api, ApiError , errMsg } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useActiveFlat } from "../lib/active-flat";
import type { Appliance, Booking, Chore } from "../lib/types";
import { ApplianceCard } from "../components/ApplianceCard";
import {
  Button,
  CenteredSpinner,
  EmptyState,
  ErrorBanner,
  SectionHeader,
} from "../components/ui";
import { formatDateTime, formatRelativeTime } from "../lib/format";

export function DashboardPage() {
  const { user } = useAuth();
  const { flat } = useActiveFlat();
  const flatId = flat!.id;

  const [appliances, setAppliances] = useState<Appliance[] | null>(null);
  const [bookings, setBookings] = useState<Record<string, Booking[]>>({});
  const [chores, setChores] = useState<Chore[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [appliancesData, choresData] = await Promise.all([
        api.get<Appliance[]>(`/appliance/by-flat/${flatId}`),
        api.get<Chore[]>(`/chore/by-flat/${flatId}?status=PENDING&assigneeId=${user!.id}`),
      ]);
      setAppliances(appliancesData);
      setChores(choresData);

      const bookingResults = await Promise.all(
        appliancesData.map(async (a) => {
          try {
            const list = await api.get<Booking[]>(`/appliance/${a.id}/bookings`);
            return [a.id, list] as const;
          } catch {
            return [a.id, []] as const;
          }
        }),
      );
      setBookings(Object.fromEntries(bookingResults));
    } catch (err) {
      setError(errMsg(err, "Couldn't load the dashboard."));
    }
  }, [flatId, user]);

  useEffect(() => {
    load();
  }, [load]);

  const completeChore = async (chore: Chore) => {
    try {
      await api.patch<Chore>(`/chore/${chore.id}`, { status: "DONE" });
      setChores((prev) => (prev ? prev.filter((c) => c.id !== chore.id) : prev));
    } catch (err) {
      setError(errMsg(err, "Couldn't mark chore done."));
    }
  };

  const upcomingFor = (applianceId: string): Booking | null => {
    const list = bookings[applianceId] ?? [];
    const future = list
      .filter((b) => b.status !== "CANCELLED" && b.status !== "DONE")
      .sort((a, b) => new Date(a.slotStart).getTime() - new Date(b.slotStart).getTime());
    return future[0] ?? null;
  };

  const myChores = chores?.filter((c) => c.status !== "DONE").slice(0, 5) ?? [];

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              Hi {user!.name?.split(" ")[0]}
            </h1>
            <p className="text-sm text-slate-500">Here's what's happening in {flat!.name}.</p>
          </div>
        </div>
      </div>

      <ErrorBanner message={error} />

      <section>
        <SectionHeader title="Appliances" subtitle="Live state from the laundry room and kitchen." />
        {appliances === null ? (
          <CenteredSpinner />
        ) : appliances.length === 0 ? (
          <EmptyState
            title="No appliances yet"
            description="An admin can add the washer, dryer, and dishwasher in Settings."
            action={
              <Link to="/settings">
                <Button variant="secondary">Go to settings</Button>
              </Link>
            }
          />
        ) : (
          <motion.div
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
            initial="hidden"
            animate="show"
            variants={{ show: { transition: { staggerChildren: 0.07 } } }}
          >
            {appliances.map((a) => (
              <motion.div
                key={a.id}
                variants={{ hidden: { opacity: 0, y: 12 }, show: { opacity: 1, y: 0, transition: { duration: 0.25 } } }}
              >
                <ApplianceCard
                  appliance={a}
                  upcomingBooking={upcomingFor(a.id)}
                  onChanged={load}
                />
              </motion.div>
            ))}
          </motion.div>
        )}
      </section>

      <section>
        <SectionHeader
          title="Your chores"
          subtitle="The top things assigned to you right now."
          action={
            <Link to="/chores" className="text-sm font-medium text-brand-600 hover:text-brand-700">
              See all →
            </Link>
          }
        />
        {chores === null ? (
          <CenteredSpinner />
        ) : myChores.length === 0 ? (
          <EmptyState
            title="All clear"
            description="You don't have any pending chores. Treat yourself."
          />
        ) : (
          <ul className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <AnimatePresence initial={false}>
            {myChores.map((c, idx) => (
              <motion.li
                key={c.id}
                layout
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className={`flex items-center gap-3 overflow-hidden px-4 py-3 ${
                  idx > 0 ? "border-t border-slate-100" : ""
                }`}
              >
                <button
                  onClick={() => completeChore(c)}
                  aria-label={`Mark ${c.name} done`}
                  className="grid h-6 w-6 shrink-0 place-items-center rounded-full border-2 border-slate-300 text-transparent transition hover:border-brand-500 hover:bg-brand-50 hover:text-brand-600"
                >
                  <LuCheck className="h-3.5 w-3.5" />
                </button>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-slate-900">{c.name}</div>
                  <div className="text-xs text-slate-500">
                    {c.status === "ESCALATED" && (
                      <span className="mr-2 font-medium text-rose-600">Escalated</span>
                    )}
                    {c.dueAt
                      ? `Due ${formatRelativeTime(c.dueAt)}`
                      : `Added ${formatRelativeTime(c.createdAt)}`}
                  </div>
                </div>
                {c.dueAt && (
                  <div className="hidden text-right text-xs text-slate-400 sm:block">
                    {formatDateTime(c.dueAt)}
                  </div>
                )}
              </motion.li>
            ))}
            </AnimatePresence>
          </ul>
        )}
      </section>
    </div>
  );
}
